<script lang="ts">
	import {
		isGeneratingPost,
		composerPost,
		isCopywriterModalOpen,
	} from '$lib/components/TextEditor/PostComposer/composerStore';
	import { _ } from 'svelte-i18n';
	import TooltipButton from '../components/TooltipButton.svelte';
	import CopywriterModalHeader from './components/CopywriterModalHeader.svelte';
	import CopywriterPromptSection from './components/CopywriterPrompt.svelte';
	import CopywriterLabel from './components/CopywriterLabel.svelte';
	import CopywriterActionButtons from './components/CopywriterActionButtons.svelte';
	import { getHubStores } from '$lib/stores/hubStores';
	import { createAssistantClient } from '$lib/ai/AssistantClient';
	import type { AssistantClientConfig, AssistantClient } from '$lib/types/AssistantClient';
	import type { Readable } from 'svelte/store';
	import type { Editor } from 'svelte-tiptap';
	import { sendPromptToAssistant } from './commands/sendPromptToAssistant';
	import { processAssistantResponse } from './commands/processAssistantResponse';
	import { isValidPrompt } from './helpers/promptValidation';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import { truncatePrompt } from '$lib/components/TextEditor/PostComposer/Copywriter/helpers/promptValidation';
	import { onMount, onDestroy } from 'svelte';
	import { derived, get, readonly } from 'svelte/store';
	import { editorStoreToPost } from '$textEditor/utils/editorToPost';
	import { addIdToPostChildren } from '$lib/ai/post/utils/addIdToPostChildren';
	import { fade } from 'svelte/transition';
	import ScreenSize from '$lib/utils/ScreenSizes';

	const { hub, pod, alidaJwt } = getHubStores();

	export let editor: Readable<Editor>;

	const isPostEnhancementEnabled = $hub.features['Toggles+HubsAICopywriterPostEnhancement'];

	const editorPostChildren = readonly(
		derived(editor, ($editor) => {
			if (!$editor) return [];
			const postChildren = editorStoreToPost($editor).children;
			return addIdToPostChildren(postChildren);
		})
	);

	let showPromptError = false;
	let hasAIResponseError = false;
	let isJwtExpired = false;

	let prompt = '';
	let assistantClient: AssistantClient;
	let modalRef: HTMLElement | null = null;
	let buttonRef: HTMLElement | null = null;
	$: isGenerateButtonDisabled = prompt === '' || $isGeneratingPost;

	onMount(() => {
		const agentName = isPostEnhancementEnabled ? 'copywriter_agent_v3' : 'copywriter_agent';

		assistantClient = createAssistantClient({
			pod,
			appName: 'hubs',
			applicationId: $hub.application_id,
			getJwt: async () => {
				if (!alidaJwt) {
					throw new Error('JWT is not available');
				}
				return alidaJwt;
			},
			agentName,
		} satisfies AssistantClientConfig);

		assistantClient.state.loading.subscribe((loading) => {
			$isGeneratingPost = loading;
		});

		assistantClient.state.isJwtExpired.subscribe((jwtExpired) => {
			isJwtExpired = jwtExpired;
		});
	});

	onDestroy(() => {
		modalRef = null;
	});

	const clearErrors = () => {
		showPromptError = false;
		hasAIResponseError = false;
	};

	const handleGeneratePost = async () => {
		clearErrors();

		if (!isValidPrompt(prompt)) {
			showPromptError = true;
			return;
		}

		await sendPromptToAssistant(
			prompt,
			assistantClient,
			isPostEnhancementEnabled ? $editorPostChildren : undefined
		);

		// Check if the request was cancelled before showing errors
		const wasCancelled = get(assistantClient.state.cancelled);
		if (wasCancelled) {
			return;
		}

		const hasInternalAIServerError = get(assistantClient.state.hasError);
		if (hasInternalAIServerError) {
			toastMessages.danger(
				$_('hubs.copywriter.errors.generic_error', { values: { prompt: truncatePrompt(prompt) } })
			);
			return;
		}

		const generatedPost = processAssistantResponse(
			assistantClient,
			composerPost,
			$editorPostChildren
		);
		if (!generatedPost || !generatedPost.content) {
			hasAIResponseError = true;
			return;
		}

		closeModal();
		$editor.commands.setContent(generatedPost.content);
		$editor.commands.focus('start');
	};

	const handleStopGenerating = () => {
		assistantClient.abort();
	};

	const closeModal = () => {
		handleStopGenerating();
		$isCopywriterModalOpen = false;
		prompt = '';
		clearErrors();
	};

	const toggleModal = () => {
		if ($isCopywriterModalOpen) {
			closeModal();
		} else {
			$isCopywriterModalOpen = true;
		}
	};

	const handleClickOutside = () => {
		if ($isGeneratingPost) return;
		closeModal();
	};

	const getModalPosition = () => {
		if (!buttonRef || !modalRef) return {};

		const margin = 28;

		const buttonRect = buttonRef.getBoundingClientRect();
		const modalSize = modalRef.getBoundingClientRect();
		const modalWidth = modalSize.width;
		const viewportWidth = window.innerWidth;

		// Default position
		let top = buttonRect.top - modalSize.height - margin;
		let left = buttonRect.left - modalWidth / 3;

		// Ensure modal stays within viewport while editing an existing post
		if (left + modalWidth + margin > viewportWidth) {
			left = viewportWidth - modalWidth - margin;
		}
		// If viewport is small, center the modal
		if (viewportWidth < ScreenSize.smallScreen) {
			left = viewportWidth / 2 - modalWidth / 2;
		}

		return {
			top: `${top}px`,
			left: `${left}px`,
		};
	};

	// Set modal position
	$: if ($isCopywriterModalOpen && modalRef) {
		const updatePosition = () => {
			if (modalRef) {
				const position = getModalPosition();
				if (position.top) modalRef.style.top = position.top;
				if (position.left) modalRef.style.left = position.left;
			} else {
				requestAnimationFrame(updatePosition);
			}
		};
		requestAnimationFrame(updatePosition);
	}
</script>

<div bind:this={buttonRef} class="copywriter-button-container">
	<TooltipButton
		onClick={toggleModal}
		imageSrc="/_static/AI/magic_button.svg"
		tooltipText={$_('hubs.copywriter.title')}
		showTooltip={!$isCopywriterModalOpen}
		isActive={$isCopywriterModalOpen} />
</div>

{#if $isCopywriterModalOpen}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<div on:click={handleClickOutside} class="copywriter-modal-background" />

	<div bind:this={modalRef} class="copywriter-modal" transition:fade={{ duration: 100 }}>
		<CopywriterModalHeader {hasAIResponseError} onClose={closeModal} {isJwtExpired} />
		<CopywriterPromptSection bind:prompt disabled={$isGeneratingPost} />
		<CopywriterLabel {showPromptError} />
		<CopywriterActionButtons
			{handleGeneratePost}
			{isGenerateButtonDisabled}
			{handleStopGenerating} />
	</div>
{/if}

<style>
	.copywriter-modal-background {
		position: fixed;
		z-index: var(--z-index-Modal);
		inset: 0;
	}

	.copywriter-modal {
		display: flex;
		position: fixed;
		z-index: calc(var(--z-index-Modal) + 100);
		flex-direction: column;
		inline-size: 60rem;
		padding: 3rem;
		border: 2px solid transparent;
		border-radius: 2rem;
		background: linear-gradient(white, white) padding-box,
			linear-gradient(135deg, #09c1d9, #054f9a) border-box;
		box-shadow: 0 20px 25px -5px rgb(0 0 0 / 10%), 0 10px 10px -5px rgb(0 0 0 / 4%);
		gap: 1rem;
		pointer-events: auto;
	}

	.copywriter-button-container {
		padding-block-start: 0.5rem;
	}
</style>
